import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Avatar,
  Alert
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyIcon from '@mui/icons-material/Key';

export default function SessionLockModal({ open, onUnlock, onLogout, username }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const displayName = username || sessionStorage.getItem('loggedInUsername') || 'Usuario';

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!password.trim()) {
      setErrorMsg('Por favor escribe tu contraseña');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      await onUnlock(password);
      setPassword('');
      setErrorMsg('');
    } catch (err) {
      setErrorMsg(err.message || 'Contraseña incorrecta');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutClick = () => {
    if (onLogout) onLogout();
  };

  return (
    <Dialog
      open={Boolean(open)}
      disableEscapeKeyDown
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(15, 23, 42, 0.78)',
          backdropFilter: 'blur(16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          m: 0,
          p: 2,
        }
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: '420px',
          bgcolor: '#ffffff',
          borderRadius: '24px',
          p: { xs: 3, sm: 4 },
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          animation: 'fadeInScale 0.28s ease-out',
          '@keyframes fadeInScale': {
            '0%': { opacity: 0, transform: 'scale(0.96)' },
            '100%': { opacity: 1, transform: 'scale(1)' }
          }
        }}
      >
        {/* Avatar / Ícono de Candado */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Avatar
            sx={{
              width: 68,
              height: 68,
              bgcolor: '#0655A5',
              boxShadow: '0 8px 24px rgba(6, 85, 165, 0.35)',
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 34, color: '#ffffff' }} />
          </Avatar>
        </Box>

        {/* Título y Usuario */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: '#0f172a',
            letterSpacing: '-0.02em',
            mb: 0.5,
            fontSize: '1.25rem'
          }}
        >
          Sesión Bloqueada por Inactividad
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            mb: 2.5,
            fontSize: '0.82rem',
            lineHeight: 1.45
          }}
        >
          Hola <strong style={{ color: '#0655A5' }}>{displayName}</strong>. Tu pantalla se bloqueó tras 15 minutos sin actividad. Escribe tu contraseña para reanudar tu trabajo exactamente donde lo dejaste.
        </Typography>

        {errorMsg && (
          <Alert
            severity="error"
            sx={{
              width: '100%',
              mb: 2,
              borderRadius: '12px',
              fontSize: '0.78rem',
              py: 0.5
            }}
          >
            {errorMsg}
          </Alert>
        )}

        {/* Input Contraseña */}
        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          placeholder="Escribe tu contraseña"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errorMsg) setErrorMsg('');
          }}
          autoFocus
          disabled={loading}
          autoComplete="current-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <KeyIcon sx={{ color: '#0655A5', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: '14px',
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#ffffff' },
              '&.Mui-focused': { bgcolor: '#ffffff' }
            }
          }}
          sx={{ mb: 2 }}
        />

        {/* Botón Desbloquear */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || !password.trim()}
          sx={{
            py: 1.3,
            bgcolor: '#0655A5',
            color: '#ffffff',
            borderRadius: '14px',
            fontWeight: 700,
            fontSize: '0.88rem',
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(6, 85, 165, 0.3)',
            '&:hover': {
              bgcolor: '#054487',
              boxShadow: '0 6px 18px rgba(6, 85, 165, 0.4)',
            },
            '&:disabled': {
              bgcolor: '#cbd5e1',
              color: '#ffffff'
            }
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: '#ffffff' }} />
          ) : (
            'Desbloquear y Continuar'
          )}
        </Button>

        {/* Salir / Cerrar Sesión */}
        <Button
          onClick={handleLogoutClick}
          startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
          sx={{
            mt: 2,
            color: '#64748b',
            textTransform: 'none',
            fontSize: '0.78rem',
            fontWeight: 600,
            '&:hover': {
              color: '#dc2626',
              bgcolor: 'transparent'
            }
          }}
        >
          Cerrar sesión completamente
        </Button>
      </Box>
    </Dialog>
  );
}
